import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Panel, Spinner, Typography } from "@/components/atoms";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { usePrompt } from "@/hooks";
import { useVehicleTypeMigrationCheck } from "@/hooks/useVehicleTypeMigrationCheck";
import { useUpdateVehicleMutation } from "@/redux/apis/vehicle/vehicleApi";
import { addToast } from "@/utils";
import { PageHeader } from "../../PageHeader/PageHeader";
import { VehicleTypesSetupModal } from "../VehicleTypesSetupModal";
import { Sidebar } from "./Sidebar";
import { EditVehicleProvider, useEditVehicleContext } from "./context";
import { VehicleDetails } from "./fields/VehicleDetails";
import { VehicleImage } from "./fields/VehicleImage";
import { EditVehicleFormData, schema } from "./form";

export const EditVehiclePanel = () => {
  return (
    <EditVehicleProvider>
      <PageHeader.Actions />
      <Content />
    </EditVehicleProvider>
  );
};

const Content = () => {
  const navigate = useNavigate();
  const { vehicle } = useEditVehicleContext();
  const { needsMigration: needsTypeMigration, valid: typesValid, total: typesTotal } = useVehicleTypeMigrationCheck();

  const defaults = useMemo(() => {
    return {
      avatar: vehicle.avatar,
      hasMarkings: vehicle.hasMarkings ?? false,
      vehicleTypeUuid: vehicle.vehicleType?.uuid ?? "",
      nickname: vehicle.identifier ?? undefined,
      capacity: vehicle.capacity.pax,
      bags: vehicle.capacity.bags,
      vehicleOwnerFee: vehicle.maintenanceFee,
    };
  }, [vehicle]);

  const form = useForm<EditVehicleFormData>({
    resolver: zodResolver(schema),
    defaultValues: defaults,
    shouldFocusError: true,
    mode: "all",
  });

  usePrompt({ when: form.formState.isDirty, message: "Changes will not be saved. Do you want to proceed?" });

  return (
    <>
      <FormProvider {...form}>
        <div className="flex flex-col items-start gap-3 xl:flex-row">
          <Panel className="flex w-full flex-col gap-5">
            <div className="flex flex-col">
              <Typography variant="h3">Edit Vehicle Details</Typography>
              <Typography className="text-neutral-dark-gray">
                Update your vehicle information anytime to make sure everything stays accurate
              </Typography>
            </div>
            <EditVehicleForm />
          </Panel>
          <Sidebar />
        </div>
      </FormProvider>
      <VehicleTypesSetupModal open={needsTypeMigration} onClose={() => navigate("../")} valid={typesValid} total={typesTotal} />
    </>
  );
};

const EditVehicleForm = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const newVehicle = searchParams.get("newVehicle") === "1";
  const [editVehicle, { isLoading }] = useUpdateVehicleMutation();
  const { vehicle } = useEditVehicleContext();
  const form = useFormContext<EditVehicleFormData>();

  const handleCancel = () => {
    if (newVehicle) navigate("../../");
    else navigate("./../");
  };

  const handleSave = async (data: EditVehicleFormData) => {
    editVehicle({
      id: vehicle.uuid,
      data: {
        identifier: data.nickname ?? "",
        has_markings: data.hasMarkings,
        capacity_pax: data.capacity,
        capacity_bags: data.bags,
        maintenance_fee: data.vehicleOwnerFee,
        vehicle_type_uuid: data.vehicleTypeUuid,
        documents: vehicle.documents.map((d) => ({ uuid: d.uuid, expiry_date: d.expiryDate, document_type: d.documentType })),
        categories: vehicle.categories.map((c) => ({ uuid: c.uuid })),
      },
    })
      .unwrap()
      .then(() => {
        addToast("success", "Vehicle successfully updated");
        setSearchParams({});
      })
      .catch((e) => {
        getErrorMessages(e).forEach((m) => addToast("danger", m));
      });
  };

  return (
    <form onSubmit={form.handleSubmit(handleSave)} id="edit-vehicle-form" className="flex flex-col gap-5">
      <VehicleImage vehicle={vehicle} />
      <VehicleDetails vehicle={vehicle} />
      <div className="flex justify-end gap-4">
        <Button onClick={handleCancel} variant="secondary" size="lg" disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="primary"
          size="lg"
          type="submit"
          form="edit-vehicle-form"
          disabled={isLoading || !form.formState.isDirty}
        >
          {isLoading ? <Spinner /> : "Save Changes"}
        </Button>
      </div>
    </form>
  );
};
